import * as React from 'react'

import type { Temporal } from '@js-temporal/polyfill'

import {
  computeCampaignDayNumber,
  filterActiveCampaigns,
  filterVisibleTemplates,
} from '../campaigns/campaignProcessing'
import {
  getTemplateDayInCampaign,
  isDripCampaign,
  isTemplateInCampaign,
  isTemplateVisibleInCampaign,
} from '../campaigns/utils'
import type { Template, TemplateCampaign } from '../types'
import { usePostTemplates } from './usePostTemplates'

export interface TemplateLibraryData {
  loading: boolean
  templates: Template[]
  visibleTemplates: Template[]
  activeCampaigns: TemplateCampaign[]
  primaryCampaign: TemplateCampaign | undefined
  isTemplateVisibleInCampaign: (
    template: Template,
    campaign: TemplateCampaign,
  ) => boolean
  getTemplateCampaignTags: (template: Template) => string[]
}

export const useTemplateLibraryData = (
  currentTime: Temporal.ZonedDateTime,
): TemplateLibraryData => {
  const { templates, campaigns: allCampaigns, loading } = usePostTemplates()

  const activeCampaigns = React.useMemo(
    () => filterActiveCampaigns(allCampaigns, currentTime),
    [allCampaigns, currentTime],
  )

  const visibleTemplates = React.useMemo(
    () =>
      filterVisibleTemplates(
        templates,
        allCampaigns,
        activeCampaigns,
        currentTime,
      ),
    [templates, allCampaigns, activeCampaigns, currentTime],
  )

  const isTemplateVisibleInCampaignFn = React.useCallback(
    (template: Template, campaign: TemplateCampaign): boolean =>
      isTemplateVisibleInCampaign(template, {
        ...campaign,
        currentDayNumber: computeCampaignDayNumber(campaign, currentTime),
      }),
    [currentTime],
  )

  const getTemplateCampaignTags = React.useCallback(
    (template: Template): string[] => {
      const tags: string[] = []

      for (const campaign of activeCampaigns) {
        if (!isTemplateInCampaign(template, campaign)) continue

        tags.push(campaign.title ?? campaign.name)

        if (isDripCampaign(campaign)) {
          const dayNumber = getTemplateDayInCampaign(template, campaign)
          if (dayNumber !== undefined) {
            tags.push(`Day ${dayNumber}`)
          }
        }
      }

      return tags
    },
    [activeCampaigns],
  )

  return {
    loading,
    templates,
    visibleTemplates,
    activeCampaigns,
    primaryCampaign: activeCampaigns[0],
    isTemplateVisibleInCampaign: isTemplateVisibleInCampaignFn,
    getTemplateCampaignTags,
  }
}
